import { useState } from "react";
import { CONTEXT_LIMIT_MESSAGE } from "../../../shared/contextLimit";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";
import { DefaultIcon, HoverIcon, ToggleIconContainer } from "../ToggleIcon";
import {
  CollapsibleHeader,
  CollapsibleHeaderInner,
  ExpandedContent,
  ExpandInner,
  ExpandWrapper,
} from "./Expand";

const systemMessageIconByPrefix: [prefix: string, icon: IconName][] = [
  ["Saved:", "save"],
  ["Restored", "history"],
  ["Workflow renamed", "pencil"],
];

export function systemMessageIcon(text: string): IconName {
  if (text === CONTEXT_LIMIT_MESSAGE) return "brain";
  for (const [prefix, icon] of systemMessageIconByPrefix) {
    if (text.startsWith(prefix)) return icon;
  }
  return "info";
}

// Git-push system messages lead with the author's name, so we match the stable
// trailing phrase rather than a prefix.
export function isPushMessage(text: string): boolean {
  return text.split("\n")[0]?.endsWith("pushed from local") ?? false;
}

function CollapsibleSystemMessage({
  text,
  icon,
}: {
  text: string;
  icon: IconName;
}) {
  const [expanded, setExpanded] = useState(false);
  const lines = text.split("\n");
  const summary = lines[0];
  const details = lines.slice(1).filter(Boolean).join("\n");

  if (!details) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          height: 28,
          gap: 4,
          padding: "6px 10px 6px 8px",
          borderRadius: 8,
          width: "fit-content",
          marginTop: 16,
        }}
      >
        <Icon name={icon} size={12} color="var(--text-secondary)" />
        <Text variant="text-small" color="var(--text-secondary)">
          {summary}
        </Text>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: 16,
      }}
    >
      <CollapsibleHeader onPress={() => setExpanded((prev) => !prev)}>
        <CollapsibleHeaderInner>
          <ToggleIconContainer aria-hidden>
            <DefaultIcon>
              <Icon name={icon} size={12} color="var(--text-secondary)" />
            </DefaultIcon>
            <HoverIcon>
              <Icon
                name={expanded ? "minus" : "plus"}
                size={12}
                color="var(--text-secondary)"
              />
            </HoverIcon>
          </ToggleIconContainer>
          <Text variant="text-small" color="var(--text-secondary)">
            {summary}
          </Text>
        </CollapsibleHeaderInner>
      </CollapsibleHeader>
      <ExpandWrapper data-expanded={expanded}>
        <ExpandInner data-expanded={expanded}>
          <ExpandedContent>
            <Text variant="text-small" color="var(--text-secondary)">
              <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {details}
              </span>
            </Text>
          </ExpandedContent>
        </ExpandInner>
      </ExpandWrapper>
    </div>
  );
}

export function MergeMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="merge" />;
}

export function PushMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="laptop" />;
}

export function GitSyncMessage({ text }: { text: string }) {
  return <CollapsibleSystemMessage text={text} icon="git-branch" />;
}
